import React, { useRef, useEffect, useState } from "react";
import DataComponent from "./DateComponent";
import { fetchHotelListByGroup } from "../../services/accommodationApi";
import { useTranslation } from "../../context/TranslationContext";

const SearchForm = ({ selectedDates, setSelectedDates, showGuestSelector, setShowGuestSelector, guests, setGuests, childAges, setChildAges, promoCode = '', setPromoCode, showAdultsOption = true, showChildrenOption = true, openCalendarSignal = 0, onSearch, isLoading = false, groupId, accommodationId, onAccommodationChange, currentLanguage, isAccommodationDropDown = 1, accommodationDetail }) => {
  const { t } = useTranslation();
  const containerRef = useRef();
  const [showAccommodationDropdown, setShowAccommodationDropdown] = useState(false);
  const [accommodations, setAccommodations] = useState([]);
  const [isLoadingAccommodations, setIsLoadingAccommodations] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);

  // Fetch accommodations when groupId is available
  useEffect(() => {
    const fetchAccommodations = async () => {
      if (!groupId) {
        setAccommodations([]);
        return;
      }

      setIsLoadingAccommodations(true);
      try {
        const langId = currentLanguage?.id || currentLanguage?.lang_id || 1;
        const accList = await fetchHotelListByGroup(groupId, langId);
        setAccommodations(accList);
        
        // Set selected accommodation if accommodationId matches
        if (accommodationId && accList.length > 0) {
          const found = accList.find(acc => 
            acc.accommodationId === parseInt(accommodationId) || 
            acc.AccommodationId === parseInt(accommodationId)
          );
          if (found) {
            setSelectedAccommodation(found);
          }
        }
      } catch (error) {
        setAccommodations([]);
      } finally {
        setIsLoadingAccommodations(false);
      }
    };

    fetchAccommodations();
  }, [groupId, accommodationId, currentLanguage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAccommodationDropdown &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowAccommodationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAccommodationDropdown]);

  const handleAccommodationSelect = (accommodation) => {
    setSelectedAccommodation(accommodation);
    setShowAccommodationDropdown(false);
    if (onAccommodationChange) {
      onAccommodationChange(accommodation.accommodationId || accommodation.AccommodationId);
    }
  };

  return (
    
    <div className="max-w-7xl mx-auto">
              <div className="bg-white shadow-xl border border-gray-100 rounded-xl py-2 px-2 card-head">
                {/* Search Form */}
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 lg:gap-3">
                    {/* Location / Accommodation Dropdown */}
                    {Number(isAccommodationDropDown) !== 0 && <div className="lg:col-span-1" ref={containerRef}>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
                        {groupId ? t('search.selectHotel') : t('search.destination')}
                      </label>
                      <div className="relative">
                        {groupId && accommodations.length > 0 && Number(isAccommodationDropDown) !== 0 ? (
                          <>
                            <svg
                              className="absolute left-2 top-2 h-6 w-4 text-gray-400 "
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowAccommodationDropdown(!showAccommodationDropdown);
                              }}
                              className="h-10 w-full pl-7 pr-8 py-1.5 text-left text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-all duration-200 bg-white flex items-center justify-between"
                            >
                              <span className="truncate text-gray-900">
                                {selectedAccommodation
                                  ? (selectedAccommodation.accommodationName || selectedAccommodation.AccommodationName)
                                  : t('search.selectHotel')}
                              </span>
                              <svg
                                className={`h-3 w-3 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                                  showAccommodationDropdown ? "rotate-180" : ""
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                            {showAccommodationDropdown && (
                              <div 
                                className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] max-h-60 overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {isLoadingAccommodations ? (
                                  <div className="p-4 text-center text-xs text-gray-500">
                                    {t('search.loadingHotels')}
                                  </div>
                                ) : accommodations.length > 0 ? (
                                  <ul className="py-1 relative">
                                    {accommodations.map((acc) => {
                                      const accId = acc.accommodationId || acc.AccommodationId;
                                      const accName = acc.accommodationName || acc.AccommodationName;
                                      const isSelected = selectedAccommodation && 
                                        (selectedAccommodation.accommodationId === accId || 
                                         selectedAccommodation.AccommodationId === accId);
                                      
                                      // Check if this accommodation has limited rooms (for "Last Room" badge)
                                      const showLastRoom = acc.totalroom && acc.totalroom <= 3;
                                      const lastRoomCount = acc.totalroom || 0;
                                      
                                      return (
                                        <li key={accId} className="relative isolate">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleAccommodationSelect(acc);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors relative ${
                                              isSelected ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900'
                                            }`}
                                            style={{ zIndex: 10 }}
                                          >
                                            <div className="flex flex-col relative">
                                              <span className="truncate relative" style={{ zIndex: 11 }}>{accName}</span>
                                              {showLastRoom && lastRoomCount > 0 && (
                                                <span
                                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-teal-500 text-white w-fit mt-1 relative"
                                                  style={{ zIndex: 1 }}
                                                >
                                                  {lastRoomCount === 1 ? t('search.lastRoom') : t('search.lastRooms').replace('{n}', lastRoomCount)}
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <div className="p-4 text-center text-xs text-gray-500">
                                    {t('search.noHotelsAvailable')}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <svg
                              className="absolute left-2 top-2 h-6 w-4 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <input
                              type="text"
                              value="Abu Dhabi, UAE"
                              className="h-10 w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-all duration-200 bg-white"
                              readOnly
                            />
                          </>
                        )}
                      </div>
                    </div>}

                    {/* Date Range */}
              <DataComponent
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                openSignal={openCalendarSignal}
                onApply={onSearch}
              />


                    {/* <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
                        Check-in
                      </label>
                      <input
                        type="date"
                        value={selectedDates.checkIn}
                        onChange={(e) =>
                          setSelectedDates({
                            ...selectedDates,
                            checkIn: e.target.value,
                          })
                        }
                        className="h-10 w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-all duration-200 bg-white"
                      />
                    </div>

                    <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
                        Check-out
                      </label>
                      <input
                        type="date"
                        value={selectedDates.checkOut}
                        onChange={(e) =>
                          setSelectedDates({
                            ...selectedDates,
                            checkOut: e.target.value,
                          })
                        }
                        className="h-10 w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-all duration-200 bg-white"
                      />
                    </div> */}

                    {/* Guests */}
                    <div className="lg:col-span-1 relative guest-selector-container">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
                        {t('search.guests')}
                      </label>
                      <div className="relative">
                        <svg
                          className="absolute left-2 top-2 h-5 w-5 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                        </svg>
                        <button
                          onClick={() =>
                            setShowGuestSelector(!showGuestSelector)
                          }
                          className="h-10 w-full pl-7 pr-6 py-1.5 text-left flex items-center justify-between text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 transition-all duration-200"
                        >
                          <span className="text-xs text-gray-900 truncate">
                            {showAdultsOption && `${guests.adults} ${guests.adults > 1 ? t('search.adults') : t('search.adult')}`}
                            {showChildrenOption && `${showAdultsOption ? ', ' : ''}${guests.children} ${guests.children !== 1 ? t('search.children') : t('search.child')}`}
                          </span>
                          <svg
                            className={`h-3 w-3 transition-transform duration-200 flex-shrink-0 ${
                              showGuestSelector ? "rotate-180" : ""
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>

                        {showGuestSelector && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl p-4 z-[9999] shadow-2xl">
                            <div className="space-y-3">
                              {showAdultsOption && (
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-gray-900 font-extralight text-sm">
                                    {t('search.adults')}
                                  </span>
                                </div>
                                <div className="flex items-center ">
                                  <button
                                    onClick={() =>
                                      setGuests({
                                        ...guests,
                                        adults: Math.max(1, guests.adults - 1),
                                      })
                                    }
                                    className=" w-6 h-6 rounded bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
                                    disabled={guests.adults <= 1}
                                  >
                                    <svg
                                      className="h-3 w-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                  <span className="text-gray-900 w-6 text-center font-extralight text-sm">
                                    {guests.adults}
                                  </span>
                                  <button
                                    onClick={() => {
                                      const maxAdults = Number(accommodationDetail?.MaxAdultQty || accommodationDetail?.maxAdultQty) || 5;
                                      setGuests({
                                        ...guests, adults: Math.min(maxAdults, guests.adults + 1),
                                      });
                                    }}
                                    className="w-6 h-6 rounded avablity-btn border text-white flex items-center justify-center transition-colors disabled:opacity-50"
                                    disabled={guests.adults >= (Number(accommodationDetail?.MaxAdultQty || accommodationDetail?.maxAdultQty) || 5)}
                                  >
                                    <svg
                                      className="h-3 w-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              )}
                              {showChildrenOption && (
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-gray-900 font-extralight text-sm">
                                    {t('search.children')}
                                  </span>
                                  {(accommodationDetail?.FreeChild || accommodationDetail?.freeChild) ? (
                                    <p className="text-xs text-gray-500">
                                      Age 0-{accommodationDetail?.FreeChild || accommodationDetail?.freeChild}
                                    </p>
                                  ) : null}
                                </div>
                                <div className="flex items-center ">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const newChildren = Math.max(0, guests.children - 1);
                                      // Update child ages array first
                                      if (newChildren < childAges.length) {
                                        setChildAges(childAges.slice(0, newChildren));
                                      }
                                      // Then update guests count
                                      setGuests({
                                        ...guests,
                                        children: newChildren,
                                      });
                                    }}
                                    className="w-6 h-6 rounded bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
                                    disabled={guests.children <= 0}
                                    type="button"
                                  >
                                    <svg
                                      className="h-3 w-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                  <span className="text-gray-900 w-6 text-center font-extralight text-sm">
                                    {guests.children}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const maxChildren = Number(accommodationDetail?.MaxChildQty || accommodationDetail?.maxChildQty) || 4;
                                      const newChildren = Math.min(maxChildren, guests.children + 1);
                                      // Update child ages array first to ensure it's the right length
                                      const updatedAges = [...childAges];
                                      if (newChildren > updatedAges.length) {
                                        updatedAges.push(1); // Add default age 1 for new child
                                      }
                                      setChildAges(updatedAges);
                                      setGuests({
                                        ...guests,
                                        children: newChildren,
                                      });
                                    }}
                                    className="w-6 h-6 rounded avablity-btn border text-white flex items-center justify-center transition-colors disabled:opacity-50"
                                    disabled={guests.children >= (Number(accommodationDetail?.MaxChildQty || accommodationDetail?.maxChildQty) || 4)}
                                    type="button"
                                  >
                                    <svg
                                      className="h-3 w-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              )}
                              {/* Child Age Selectors - Show only if children > 0 */}
                              {showChildrenOption && guests.children > 0 && (
                                <div className="pt-2 border-t border-gray-200 space-y-2">
                                  {Array.from({ length: guests.children }).map((_, index) => {
                                    // Ensure childAges array has enough elements
                                    const currentAge = childAges[index] || 1;
                                    return (
                                      <div key={`child-age-${index}`} className="flex items-center justify-between">
                                        <span className="text-gray-900 font-extralight text-sm">
                                          {t('search.childAge').replace('{index}', index + 1)}
                                        </span>
                                        <select
                                          value={currentAge}
                                          onChange={(e) => {
                                            const newAges = [...childAges];
                                            // Ensure array is long enough
                                            while (newAges.length <= index) {
                                              newAges.push(1);
                                            }
                                            newAges[index] = parseInt(e.target.value);
                                            setChildAges(newAges);
                                          }}
                                          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                          {Array.from({ length: Number(accommodationDetail?.FreeChild || accommodationDetail?.freeChild) || 10 }, (_, i) => i + 1).map((age) => (
                                            <option key={age} value={age}>
                                              {age} {age === 1 ? t('search.year') : t('search.years')}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {/* <div className="pt-2 border-t border-gray-200">
                                <button
                                  onClick={() => setShowGuestSelector(false)}
                                  className="w-full bg-gradient-to-r from-zinc-600 to-stone-800 text-white py-1.5 px-3 rounded text-xs font-extralight hover:from-zinc-700 hover:to-stone-900 transition-all duration-200"
                                >
                                  Done
                                </button>
                              </div> */}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Promo Code */}
                    <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 text-white tracking-wide">
                        {t('search.promoCode')}
                      </label>
                      <input
                        type="text"
                        placeholder={t('search.enterPromoCode')}
                        value={promoCode}
                        onChange={(e) => setPromoCode && setPromoCode(e.target.value)}
                        className="h-10 w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-all duration-200 bg-white"
                      />
                    </div>

                    {/* Search Button */}
                    <div className="lg:col-span-1 flex items-end">
                      <button 
                        onClick={onSearch}
                        disabled={isLoading}
                        className="h-10 w-full text-white font-medium py-2 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center space-x-2 avablity-btn border text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-sm">{t('search.searching')}</span>
                          </>
                        ) : (
                          <span className="text-sm">{t('search.checkAvailability')}</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
  );
};

export default SearchForm;
